import { create } from 'zustand';
import { api } from '../lib/api';
import { useAuthStore } from './auth';
import { DEFAULT_USER_SETTINGS, type UserSettings, type NotificationSettings, type QuietHoursSettings } from '@nudge/shared';

export type SettingsPatch = Partial<Omit<UserSettings, 'notifications' | 'quiet_hours'>> & {
  notifications?: Partial<NotificationSettings>;
  quiet_hours?: QuietHoursSettings | null;
};

interface SettingsState {
  settings: UserSettings;
  applied: boolean;
  hydrate: (settings?: UserSettings) => void;
  update: (patch: SettingsPatch) => Promise<void>;
}

function applyToDocument(s: UserSettings) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  const desired = s.theme === 'system'
    ? (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : s.theme;
  html.setAttribute('data-theme', desired);
  html.setAttribute('data-font', s.font_size);
  html.setAttribute('data-card', s.card_size);
  html.setAttribute('data-reduce-motion', String(s.reduce_motion));
  html.setAttribute('data-contrast', s.high_contrast ? 'high' : 'normal');
}

if (typeof matchMedia !== 'undefined') {
  matchMedia('(prefers-color-scheme: light)').addEventListener?.('change', () => {
    const cur = useSettingsStore.getState().settings;
    if (cur.theme === 'system') applyToDocument(cur);
  });
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: DEFAULT_USER_SETTINGS,
  applied: false,

  hydrate(s?: UserSettings) {
    const merged = s ? { ...DEFAULT_USER_SETTINGS, ...s, notifications: { ...DEFAULT_USER_SETTINGS.notifications, ...s.notifications } } : DEFAULT_USER_SETTINGS;
    set({ settings: merged, applied: true });
    applyToDocument(merged);
  },

  async update(patch) {
    const cur = get().settings;
    const next = {
      ...cur,
      ...patch,
      notifications: { ...cur.notifications, ...(patch.notifications ?? {}) },
      quiet_hours: patch.quiet_hours === undefined ? cur.quiet_hours : patch.quiet_hours,
    };
    set({ settings: next });
    applyToDocument(next);
    const auth = useAuthStore.getState();
    if (auth.user) {
      auth.setUser({ ...auth.user, settings: next });
    }
    try {
      await api.updateMe({ settings: patch as Partial<UserSettings> });
    } catch {
      /* settings stay locally; queue not used for non-mutating user prefs */
    }
  },
}));
